// ============================================================================
// ClientHeader — the client's identity bar: large avatar, name, star/favourite
// toggle (optimistic, live-synced via useStarClient), age derived from the
// birthday, and key tags (sex · occupation · status). Houses the page's primary
// actions: Edit profile, View report.
// Mobile-first: stacks on small screens, becomes a row from sm:. Rich but
// restrained motion — the whole header rises in; the star pops on toggle.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, Badge, Button, Card, PackageBadge } from "../../components";
import { IconStar } from "../../components/icons";
import { useStarClient } from "../../hooks/clients";
import { useClientPackages } from "../../hooks/packages";
import type { Client } from "../../api";
import { ageFromBirthday, fmtDate, normalizeSex } from "./lib";

interface ClientHeaderProps {
  client: Client;
  onEdit: () => void;
}

export function ClientHeader({ client, onEdit }: ClientHeaderProps) {
  const { t } = useTranslation("clientDetail");
  const reduce = useReducedMotion();
  const star = useStarClient();
  const starred = client.starred === 1;

  const packagesQuery = useClientPackages(client.id);
  const activePackage =
    (packagesQuery.data ?? []).find((p) => p.status === "active") ?? null;

  const age = ageFromBirthday(client.birthday);
  const sex = normalizeSex(client.sex);
  const sexLabel = sex ? t(`sex.${sex}`) : null;
  const isArchived = client.status === "archived";

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <Card className="relative overflow-hidden">
        {/* Ambient gradient wash in the corner — Pulse signature, subtle. */}
        <span
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-grad-coral opacity-[0.07] blur-2xl"
        />

        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <Avatar
              name={client.name}
              size="xl"
              status={isArchived ? "rest" : starred ? "online" : null}
              src={
                client.profile_photo_id != null
                  ? `/api/media/${client.profile_photo_id}`
                  : undefined
              }
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate font-display text-2xl font-extrabold tracking-tightest text-body sm:text-3xl">
                  {client.name}
                </h1>
                <StarButton
                  starred={starred}
                  name={client.name}
                  pending={star.isPending}
                  onToggle={() => star.mutate(client.id)}
                />
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {age != null && (
                  <Badge tone="volt" dot>
                    {t("header.age", { count: age })}
                  </Badge>
                )}
                {sexLabel && <Badge tone="ember">{sexLabel}</Badge>}
                {client.height_cm != null && (
                  <Badge tone="info">{t("header.height", { value: client.height_cm })}</Badge>
                )}
                {client.occupation && (
                  <Badge tone="neutral">{client.occupation}</Badge>
                )}
                {isArchived && (
                  <Badge tone="warning" solid>
                    {t("header.archived")}
                  </Badge>
                )}
                <PackageBadge pkg={activePackage} />
              </div>

              <p className="mt-2 text-xs text-body-soft">
                {t("header.clientSince", { date: fmtDate(client.created_at) })}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-2 sm:items-end">
            <Button onClick={onEdit} size="md">
              {t("header.editProfile")}
            </Button>
            <Link to={`/app/clients/${client.id}/report`}>
              <Button variant="ghost" size="md" block>
                {t("header.viewReport")}
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function StarButton({
  starred,
  name,
  pending,
  onToggle,
}: {
  starred: boolean;
  name: string;
  pending: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation("clientDetail");
  const reduce = useReducedMotion();
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      disabled={pending}
      aria-pressed={starred}
      aria-label={
        starred ? t("header.unstar", { name }) : t("header.star", { name })
      }
      whileTap={reduce ? undefined : { scale: 0.82 }}
      className={
        "grid h-10 w-10 shrink-0 place-items-center rounded-pill transition-colors " +
        (starred
          ? "text-amber"
          : "text-body-faint hover:text-amber hover:bg-amber-soft")
      }
    >
      <motion.span
        key={starred ? "on" : "off"}
        initial={reduce ? false : { scale: 0.6, rotate: starred ? -30 : 0 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 16 }}
        className="inline-flex"
      >
        <IconStar filled={starred} width={24} height={24} />
      </motion.span>
    </motion.button>
  );
}
